import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import bgImage from '../assets/images/contacthero.jpeg';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const LEADS_API = 'https://app.abhifs.com/api/public/instagram-leads';

const InstaLeadsPage = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    phone: '',
    email: '',
    dob: '',
    pan_card: '',
    employment_type: '',
    current_company: '',
    working_company: '',
    salary_per_month: '',
    ctc: '',
    source_details: '',
    priority: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name) {
      toast.error('First name and last name are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(formData).filter(([, v]) => v !== '')
      );
      await axios.post(LEADS_API, payload);
      toast.success('Details submitted successfully! We will contact you soon.');
      setFormData({
        first_name: '', middle_name: '', last_name: '', phone: '', email: '',
        dob: '', pan_card: '', employment_type: '', current_company: '',
        working_company: '', salary_per_month: '', ctc: '', source_details: '', priority: '',
      });
    } catch (error) {
      toast.error('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full border border-gray-300 rounded-md px-4 py-3 shadow-md focus:outline-none focus:ring-2 focus:ring-[#70c037] bg-white';

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white" style={{ marginTop: '-80px' }}>
      {/* Hero */}
      <section
        className="py-40 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${bgImage})`, backgroundPosition: 'center 80%' }}
      >
        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h1 className="text-4xl md:text-5xl font-semibold text-[#1f3d2b]">Insta Leads</h1>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <p className="text-gray-600 mt-3 text-lg">Share your details and get instant assistance.</p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 bg-gray-50" style={{ marginTop: '-40px' }}>
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <div className="max-w-3xl mx-auto px-6">
            <div className="flex items-center justify-center gap-4 mb-10">
              <div className="h-[1px] bg-gray-300 w-24 sm:w-40"></div>
              <h2 className="text-3xl font-semibold text-gray-800 whitespace-nowrap">Your Details</h2>
              <div className="h-[1px] bg-gray-300 w-24 sm:w-40"></div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Row 1: First / Middle / Last Name */}
              <div className="grid sm:grid-cols-3 gap-5">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">First Name <span className="text-red-500">*</span></label>
                  <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} placeholder="First Name" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Middle Name</label>
                  <input type="text" name="middle_name" value={formData.middle_name} onChange={handleChange} placeholder="Middle Name" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Last Name <span className="text-red-500">*</span></label>
                  <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Last Name" className={inputCls} />
                </div>
              </div>

              {/* Row 2: Phone / Email */}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Phone</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone Number" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email Address" className={inputCls} />
                </div>
              </div>

              {/* Row 3: DOB / PAN */}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Date of Birth</label>
                  <input type="date" name="dob" value={formData.dob} onChange={handleChange} className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">PAN Card</label>
                  <input type="text" name="pan_card" value={formData.pan_card} onChange={handleChange} placeholder="PAN Number" className={inputCls} />
                </div>
              </div>

              {/* Row 4: Employment Type / Current Company */}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Employment Type</label>
                  <select name="employment_type" value={formData.employment_type} onChange={handleChange} className={inputCls}>
                    <option value="">Select Employment Type</option>
                    <option value="salaried">Salaried</option>
                    <option value="self_employed">Self Employed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Current Company</label>
                  <input type="text" name="current_company" value={formData.current_company} onChange={handleChange} placeholder="Current Company" className={inputCls} />
                </div>
              </div>

              {/* Row 5: Working Company / Salary */}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Working Company</label>
                  <input type="text" name="working_company" value={formData.working_company} onChange={handleChange} placeholder="Working Company" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Salary Per Month (₹)</label>
                  <input type="number" name="salary_per_month" value={formData.salary_per_month} onChange={handleChange} placeholder="Monthly Salary" min="0" className={inputCls} />
                </div>
              </div>

              {/* Row 6: CTC / Priority */}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">CTC (Annual ₹)</label>
                  <input type="number" name="ctc" value={formData.ctc} onChange={handleChange} placeholder="Annual CTC" min="0" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Priority</label>
                  <select name="priority" value={formData.priority} onChange={handleChange} className={inputCls}>
                    <option value="">Select Priority</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              {/* Source Details */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">How did you find us on Instagram?</label>
                <input type="text" name="source_details" value={formData.source_details} onChange={handleChange} placeholder="E.g. Instagram ad, post, reel..." className={inputCls} />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#70c037] hover:bg-[#5ea82e] text-white font-semibold py-3 rounded-md transition-colors duration-200 disabled:opacity-60"
              >
                {submitting ? 'Submitting...' : 'Submit Details'}
              </button>
            </form>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default InstaLeadsPage;
